import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import Wrapper from '@components/wrapper.styled';
import Image, { ImageProps } from '@components/image';
import generateId from '@helpers/generate-id';
import NewsletterModal from '@components/newsletter-modal';
import getLinkConfig from '@helpers/get-link-config';
import Text from '@components/text';
import { RichTextBlock } from 'prismic-reactjs';
import { PrimaryProps } from '../module/index';
import { Item } from '../module/item-props.type';
import {
  StyledFooter,
  SocialIconWrapper,
  SocialContainer,
  MainContainer,
  LogoContainer,
  Logo,
  LinksGrid,
  LinksGridColumn,
  ItemLink,
  LinkList,
  LinkListItem,
  ExternalItemLink,
  ExternalLink,
  BottomContainer,
  StyledSpan,
  BottomLinksContainer,
  BottomLinks,
  StyledLink,
  SocialMedia,
  StyledText,
  MetaContainer,
  TopContainer,
  NewsletterContainer,
  ContactContainer,
} from './footer.styled';

const columnIdGenerator = generateId();
const navListItemIdGenerator = generateId();
const metaLinkIdGenerator = generateId();
const socialLinkIdGenerator = generateId();

interface FooterProps {
  offsetAmount: number;
}

interface LinksType {
  link_label: string;
  link: {
    url: string;
    uid: string;
    target: string;
  };
}

interface SocialMediaType {
  image: ImageProps;
  link: {
    uid: string;
    url: string;
    target: string;
  };
}

interface FooterPageInterface {
  prismicGeneralConfig: {
    data: {
      legal_brand_name?: string;
      logo?: {
        alt: string;
        url: string;
        uid: string;
      };
      links: LinksType[];
      social_media: SocialMediaType[];
      body: {
        slice_type: string;
        primary: PrimaryProps;
        items: Item[];
      }[];
    };
  };
  prismicFooterConfig: {
    data: {
      address?: { richText: RichTextBlock[] };
      telephone_number?: { richText: RichTextBlock[] };
      email?: { richText: RichTextBlock[] };
    };
  };
}

const query = graphql`
  query FooterQuery {
    prismicGeneralConfig {
      data {
        legal_brand_name
        logo {
          alt
          url
        }
        links {
          link {
            uid
            url
            target
          }
          link_label
        }
        social_media {
          image {
            url
            alt
          }
          link {
            url
            target
          }
        }
        body {
          ... on PrismicGeneralConfigDataBodyLinks {
            slice_type
            primary {
              title {
                text
              }
            }
            items {
              link {
                uid
                url
              }
              link_label
            }
          }
        }
      }
    }
    prismicFooterConfig {
      data {
        address {
          richText
        }
        telephone_number {
          richText
        }
        email {
          richText
        }
      }
    }
  }
`;

const Footer = ({ offsetAmount }: FooterProps): JSX.Element => {
  const {
    prismicGeneralConfig: {
      data: { legal_brand_name, logo, links, social_media, body },
    },
    prismicFooterConfig: {
      data: { address, telephone_number, email },
    },
  }: FooterPageInterface = useStaticQuery(query);
  const currentYear = new Date().getFullYear();
  const copyText = `© ${currentYear} ${legal_brand_name || 'PassioTech.com'} All rights reserved.`;

  return (
    <StyledFooter>
      <Wrapper>
        <TopContainer>
          <MetaContainer>
            <LogoContainer>{logo?.url && <Logo imageUrl={logo.url} />}</LogoContainer>
            <SocialContainer>
              <Text
                type="title"
                tag="h4"
                fontColor="primary"
                fontSize="body"
                stringText="Follow Us"
                margin="none"
              />
              <SocialIconWrapper>
                {social_media.map(({ image, link }) => {
                  if (!image?.url || !link?.url) {
                    return null;
                  }

                  return (
                    <SocialMedia
                      href={link?.url}
                      target={link?.target}
                      rel="noreferrer"
                      key={socialLinkIdGenerator.next().value}
                      title={
                        image?.alt !== undefined
                          ? `Follow us on ${image?.alt}`
                          : 'Follow us on social'
                      }
                    >
                      <Image url={image?.url} alt={image?.alt} />
                    </SocialMedia>
                  );
                })}
              </SocialIconWrapper>
            </SocialContainer>
          </MetaContainer>
          <MainContainer>
            <LinksGrid>
              {body.map(({ primary, items }) => {
                const columnId = columnIdGenerator.next().value;
                const columnTitleId = `link-grid-column-${columnId}`;
                return (
                  <LinksGridColumn key={columnId}>
                    <Text
                      id={columnTitleId}
                      type="title"
                      tag="h4"
                      fontColor="primary"
                      fontSize="body"
                      stringText={primary.title?.text}
                    />
                    <LinkList
                      aria-labelledby={columnTitleId}
                      aria-label={`${primary.title?.text || 'Section'} Navigation`}
                    >
                      {items.map(({ link, link_label }) => {
                        const linkConfig = getLinkConfig(link);
                        if (!linkConfig.url || !link_label) {
                          return null;
                        }

                        return (
                          <LinkListItem key={navListItemIdGenerator.next().value}>
                            {linkConfig.isExternal ? (
                              <ExternalItemLink href={linkConfig.url}>
                                {link_label}
                              </ExternalItemLink>
                            ) : (
                              <ItemLink to={linkConfig.url}>{link_label}</ItemLink>
                            )}
                          </LinkListItem>
                        );
                      })}
                    </LinkList>
                  </LinksGridColumn>
                );
              })}
            </LinksGrid>
            <NewsletterContainer>
              <ContactContainer>
                <Text
                  type="title"
                  tag="h4"
                  fontColor="primary"
                  fontSize="body"
                  stringText="Our Office"
                  margin="none"
                />
                <StyledText
                  type="description"
                  fontColor="white"
                  fontSize="small"
                  richText={address?.richText}
                />
                <StyledText
                  type="description"
                  fontColor="white"
                  fontSize="small"
                  richText={telephone_number?.richText}
                />
                <StyledText
                  type="description"
                  fontColor="white"
                  fontSize="small"
                  richText={email?.richText}
                />
              </ContactContainer>
              <NewsletterModal offsetAmount={offsetAmount} />
            </NewsletterContainer>
          </MainContainer>
        </TopContainer>
      </Wrapper>
      <Wrapper style={{ zIndex: 0 }}>
        <BottomContainer>
          <StyledSpan>{copyText}</StyledSpan>
          <BottomLinksContainer>
            <BottomLinks>
              {links.map(({ link_label, link }) => {
                const linkConfig = getLinkConfig(link);

                return (
                  <React.Fragment key={metaLinkIdGenerator.next().value}>
                    {linkConfig.isExternal ? (
                      <ExternalLink href={linkConfig.url}>{link_label}</ExternalLink>
                    ) : (
                      <StyledLink to={linkConfig.url}>{link_label}</StyledLink>
                    )}
                  </React.Fragment>
                );
              })}
            </BottomLinks>
          </BottomLinksContainer>
        </BottomContainer>
      </Wrapper>
    </StyledFooter>
  );
};

export default Footer;
